import { Box, Backdrop, CircularProgress, Chip, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

export function CustomTitle(title) {
  if (title) {
    return title
      .split(/[\s_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return title;
}

export function getYesterdayDate() {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const utcYesterday = yesterday.toISOString();
  return utcYesterday;
}

export function SectionBox({
  height,
  title,
  children,
  bgcolor = "white",
  fontWeight = 400,
  minWidth = "0px",
}) {
  return (
    <Box
      height={height}
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        padding: 2,
        backgroundColor: "white",
        position: "relative",
        bgcolor: bgcolor,
        // width:"98%"
        minWidth: minWidth,
      }}
    >
      <Box
        sx={{
          bgcolor: bgcolor,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          // backgroundColor: "white",
          position: "absolute",
          top: -15,
          left: 30,
          fontSize: "13px",
          fontWeight: fontWeight,
          px: 2,
          py: 0.5,
        }}
      >
        {title}
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

export function Loader({ loading }) {
  return (
    <Backdrop
      open={loading}
      sx={(theme) => ({
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      })}
    >
      <CircularProgress />
    </Backdrop>
  );
}

export function getFilteredData(
  value,
  originalData,
  excludedKeys = ["CorporationID"],
) {
  if (!value) return originalData;

  return originalData.filter((item) =>
    Object.entries(item).some(
      ([key, val]) =>
        !excludedKeys.includes(key) &&
        String(val).toLowerCase().includes(value.toLowerCase()),
    ),
  );
}

export async function handleSessionOut() {
  const name = "sessionout";
  window.parent.postMessage(name, "*");
  await new Promise((resolve) => setTimeout(resolve, 500));
}

// This is for getEST timing to show in pdf (/generatepdf)
export const getESTTime = () => {
  return new Date()
    .toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .replace(",", "");
};

export const OutlinedSelectButton = ({
  label,
  selected,
  onClick,
  size = "medium",
  width = "100px",
}) => {
  return (
    <Chip
      label={label}
      variant="outlined"
      size={size}
      clickable
      onClick={onClick}
      deleteIcon={selected ? <RadioButtonCheckedIcon /> : null}
      onDelete={selected ? onClick : undefined}
      sx={{
        width,
        justifyContent: "space-between",
        px: 1,
        borderColor: selected ? "primary.main" : "grey.300",
        color: selected ? "primary.main" : "text.secondary",
        fontWeight: 500,
        borderRadius: 1,

        "& .MuiChip-label": {
          flexGrow: 1,
          textAlign: "left",
          paddingLeft: 0,
          overflow: "visible",
        },

        "& .MuiChip-deleteIcon": {
          marginLeft: "auto",
          color: "primary.main",
          fontSize: 14,
          mr: 0.5,
        },
      }}
    />
  );
};

export const CustomOptions = ({
  type,
  width = "190px",
  selected,
  onClick,
  disabled,
}) => {
  return (
    <Button
      size="small"
      variant="contained"
      endIcon={
        <CheckCircleIcon
          style={{
            color: disabled ? "#d7d7d7" : selected ? "#00ba00" : "#d7d7d7",
          }}
        />
      }
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: 400,
        fontSize: "11px",
        border: selected ? "0.1px solid #f6ffe6" : "0.1px solid #d7d7d7",
        backgroundColor: disabled
          ? "#f0f0f0"
          : selected
            ? "#f6ffe6"
            : "#ffffff",
        color: disabled ? "#a0a0a0" : "#333333",
        borderRadius: "3px",
        textTransform: "none",
        width: width,
        padding: "2px 10px",
        whiteSpace: "nowrap",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        margin: "4px 0",
      }}
      onClick={disabled ? undefined : onClick}
      disableRipple
      disableElevation
    >
      <span
        title={type}
        style={{ flexGrow: 1, textAlign: "left", overflow: "hidden" }}
      >
        {type}
      </span>
    </Button>
  );
};



export default function ViewTransactionsRanges(periodInfo) {
 
    dayjs.extend(quarterOfYear);
 
    const currentDate = dayjs();
 
    const fiscalStart = periodInfo?.fiscalStartDate
        ? dayjs(periodInfo.fiscalStartDate)
        : currentDate.startOf('year');
 
    const fiscalEnd = periodInfo?.fiscalEndDate
        ? dayjs(periodInfo.fiscalEndDate)
        : currentDate.endOf('year');
 
 
    const monthsDiff = currentDate.diff(fiscalStart, 'month');
    const quarterIndex = Math.floor(monthsDiff / 3);
 
    const quarterStart = fiscalStart.add(quarterIndex * 3, 'month');
 
    const quarterEnd = quarterStart
        .add(3, 'month')
        .subtract(1, 'day')
        .endOf('day');
 
    const dateOptions = [
        {
            date: "-Select Period-",
            actualValue: {
                start: null,
                end: null
            }
        },
        {
            date: "Range",
            actualValue: { start: null, end: null }
        },
 
        {
            date: "Today",
            actualValue: {
                start: currentDate.startOf('day'),
                end: currentDate.endOf('day')
            }
        },
        {
            date: "Yesterday",
            actualValue: {
                start: currentDate.subtract(1, 'day').startOf('day'),
                end: currentDate.subtract(1, 'day').endOf('day')
            }
        },
        {
            date: "Past 30 Days",
            actualValue: {
                start: currentDate.subtract(29, 'day'),
                end: currentDate
            }
        },
        {
            date: "This Week",
            actualValue: {
                start: currentDate.startOf('week').toDate(),
                end: currentDate.endOf('week').toDate()
            }
        },
        {
            date: "This Week – to – Date",
            actualValue: {
                start: currentDate.startOf('week').toDate(),
                end: currentDate.toDate()
            }
        },
        {
            date: "Last Week",
            actualValue: {
                start: currentDate.subtract(1, 'week').startOf('week').toDate(),
                end: currentDate.subtract(1, 'week').endOf('week').toDate()
            }
        },
        {
            date: "Last Week–to–Date",
            actualValue: {
                start: currentDate.subtract(1, 'week').startOf('week').toDate(),
                end: currentDate
                    .subtract(1, 'week')
                    .endOf('day')
                    .toDate()
            }
        },
        {
            date: "This Month",
            actualValue: {
                start: currentDate.startOf('month').toDate(),
                end: currentDate.endOf('month').toDate()
            }
        },
        {
            date: "This Month – to – Date",
            actualValue: {
                start: currentDate.startOf('month').toDate(),
                end: currentDate.toDate()
            }
        },
        {
            date: "Last Month",
            actualValue: {
                start: currentDate.subtract(1, 'month').startOf('month').toDate(),
                end: currentDate.subtract(1, 'month').endOf('month').toDate()
            }
        },
        {
            date: "Last Month – to – Date",
            actualValue: {
                start: currentDate.subtract(1, 'month').startOf('month').toDate(),
                end: currentDate
                    .subtract(1, 'month')
                    .date(currentDate.date())
                    .endOf('day')
                    .toDate()
            }
        },
        {
            date: "This Quarter",
            actualValue: {
                start: quarterStart.toDate(),
                end: quarterEnd.toDate()
            }
        },
        {
            date: "This Quarter – to – Date",
            actualValue: {
                start: quarterStart.toDate(),
                end: currentDate.toDate()
            }
        },
        {
            date: "Last Quarter",
            actualValue: {
                start: quarterStart
                    .subtract(3, 'month')
                    .toDate(),
 
                end: quarterStart
                    .subtract(1, 'day')
                    .endOf('day')
                    .toDate()
            }
        },
        {
            date: "Last Quarter – to – Date",
            actualValue: {
                start: quarterStart
                    .subtract(3, 'month')
                    .toDate(),
 
                // end: quarterStart
                //     .subtract(3, 'month')
                //     .add(
                //         currentDate.diff(quarterStart, 'day'),
                //         'day'
                //     )
                //     .endOf('day')
                //     .toDate()
                end: currentDate
                    .subtract(3, 'month')
                    .endOf('day')
                    .toDate()
            }
        },
        {
            date: "This Year",
            // actualValue: {
            //     start: baseDate.startOf('year').toDate(),
            //     end: baseDate.endOf('year').toDate()
            // }
            actualValue: {
                start: fiscalStart,
                end: fiscalEnd
            }
        },
        {
            date: "This Year – to – Date",
            // actualValue: {
            //     start: baseDate.startOf('year').toDate(),
            //     end: baseDate.toDate()
            // }
            actualValue: {
                start: fiscalStart,
                end: currentDate
            }
        },
        {
            date: "Last Year",
            // actualValue: {
            //     start: baseDate.subtract(1, 'year').startOf('year').toDate(),
            //     end: baseDate.subtract(1, 'year').endOf('year').toDate()
            // }
            actualValue: {
                start: fiscalStart.subtract(1, 'year'),
                end: fiscalEnd.subtract(1, 'year')
            }
        },
        {
            date: "Last Year – to – Date",
            // actualValue: {
            //     start: baseDate.subtract(1, 'year').startOf('year').toDate(),
            //     // end: baseDate.subtract(1, 'year').month(baseDate.month()).date(baseDate.date()).endOf('day').toDate()
            //     end: baseDate
            //         .subtract(1, 'year')
            //         .endOf('day')
            //         .toDate()
            // }
            actualValue: {
                start: fiscalStart.subtract(1, 'year'),
                end: currentDate.subtract(1, 'year')
            }
        },
 
    ];
 
 
    return dateOptions;
 
}
